/**
 * Staff permission names, mirroring the API's `StaffPermissions` constants
 * (M0-33, `api/src/MainLinePro.Api/Authorization/StaffAuthorization.cs`).
 *
 * ⚠️ A **different vocabulary** from the customer app's `Permissions`, and
 * separately prefixed on purpose: the API's own comment notes that
 * `"diagnostics.manage"` versus `"staff:diagnostics.global"` should not be a
 * matter of reading carefully. That is exactly why these names do not live in
 * a package shared with the customer app — the two sets must not become
 * interchangeable strings in one namespace.
 *
 * Only the *names* live here. Which permissions the signed-in member of staff
 * actually holds is whatever `/api/v1/admin/me` returns — the persona →
 * permission matrix is never reimplemented in the browser, so the two cannot
 * drift.
 *
 * And none of this is a security boundary. Everything here decides what to
 * *show*; the API decides what may *happen*. Someone who edits their way past
 * a guard reaches an endpoint that refuses them — M1-34 asserts exactly that.
 */
export const StaffPermissions = {
  readTenants: 'staff:tenants.read',
  diagnosticsAccount: 'staff:diagnostics.account',
  diagnosticsGlobal: 'staff:diagnostics.global',
  readAudit: 'staff:audit.read',
} as const;

export type StaffPermission = (typeof StaffPermissions)[keyof typeof StaffPermissions];
